"use client"

import Link from "next/link"
import { useEffect, useMemo, useRef, useState } from "react"
import {
  CheckCircle2, XCircle, FileSpreadsheet, X, RotateCcw, Loader2, Mail,
  ExternalLink, Upload, ChevronDown, Ban,
} from "lucide-react"
import { useUploadContext } from "@/lib/upload/upload-context"
import { hasLastUpload, retryLastUpload } from "@/lib/upload/upload-bridge"
import { Toast } from "@/lib/toast"

// ──────────────────────────────────────────────────────────────────
// STAGES — discrete phases that map to a progress threshold. Each
// stage gets a row in the timeline. Lifted from the legacy phaseLabel
// helper so we can show "✓ Validated" history rather than a single
// shifting label, which is the move that makes this feel like Vercel
// deployments / GitHub Actions instead of a plain progress bar.
// ──────────────────────────────────────────────────────────────────
const STAGES = [
  { key: "upload",     label: "Uploaded",            threshold: 10  },
  { key: "validate",   label: "Parsed & validated",  threshold: 45  },
  { key: "duplicates", label: "Detecting duplicates",threshold: 75  },
  { key: "finalize",   label: "Finalizing",          threshold: 92  },
  { key: "done",       label: "Almost done",         threshold: 100 },
] as const

function stageStateFor(progress: number, idx: number) {
  const threshold = STAGES[idx].threshold
  const prevThreshold = idx === 0 ? 0 : STAGES[idx - 1].threshold
  if (progress >= threshold)        return "done" as const
  if (progress >= prevThreshold)    return "active" as const
  return "pending" as const
}

function formatElapsed(ms: number): string {
  if (ms < 1000) return "0s"
  const totalS = Math.floor(ms / 1000)
  const m = Math.floor(totalS / 60)
  const s = totalS % 60
  if (m === 0) return `${s}s`
  return `${m}:${s.toString().padStart(2, "0")}`
}

/** Mobile circular progress ring. */
function ProgressRing({ progress, color }: { progress: number; color: string }) {
  const r = 13
  const c = 2 * Math.PI * r
  const offset = c - (Math.max(0, Math.min(100, progress)) / 100) * c
  return (
    <svg className="absolute inset-0" viewBox="0 0 32 32" fill="none">
      <circle cx="16" cy="16" r={r} stroke="rgba(255,255,255,0.10)" strokeWidth="2" />
      <circle
        cx="16" cy="16" r={r}
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeDasharray={c}
        strokeDashoffset={offset}
        transform="rotate(-90 16 16)"
        style={{ transition: "stroke-dashoffset 300ms ease-out" }}
      />
    </svg>
  )
}

// ──────────────────────────────────────────────────────────────────
export default function UploadStatusChip() {
  const {
    status, progress, fileName, startedAt, errorMessage, notice, jobId,
    pending, dismissStatus,
  } = useUploadContext()

  const [open, setOpen]               = useState(false)
  const [cancelling, setCancelling]   = useState(false)
  const wrapRef                       = useRef<HTMLDivElement | null>(null)

  // Stage completion timestamps. Each stage records "when did we cross
  // its threshold?" so the timeline can show stable per-stage durations
  // instead of recomputing from `startedAt` on every tick.
  const stageTimes = useRef<Record<string, number>>({})
  useEffect(() => {
    if (status !== "processing") return
    STAGES.forEach((stage) => {
      if (progress >= stage.threshold && !stageTimes.current[stage.key]) {
        stageTimes.current[stage.key] = Date.now()
      }
    })
  }, [progress, status])
  // Reset stage history when a new job starts.
  useEffect(() => {
    if (status === "idle" || (status === "processing" && progress === 0)) {
      stageTimes.current = {}
    }
  }, [status, progress])

  // Tick once a second while processing for the live elapsed counter.
  const [, forceTick] = useState(0)
  useEffect(() => {
    if (status !== "processing" || !startedAt) return
    const id = setInterval(() => forceTick((x) => x + 1), 1000)
    return () => clearInterval(id)
  }, [status, startedAt])

  // Auto-open the panel when the upload starts so the user discovers
  // it exists (the chevron alone isn't always loud enough). Only the
  // processing-start preview auto-closes — done/error stay open until
  // the user explicitly dismisses, because those states carry critical
  // CTAs (Open dataset / Retry) that the user needs to act on. Hovering
  // the panel pauses the auto-close timer so reading isn't interrupted.
  const lastStatusRef = useRef(status)
  useEffect(() => {
    if (lastStatusRef.current === status) return
    lastStatusRef.current = status
    if (status === "idle") return
    setOpen(true)
  }, [status])

  // No auto-close — the panel stays open until the user dismisses it
  // (X button, click outside, Esc, or toggling the chip).

  // Close on Esc / outside click. Outside-click skips the bottom sheet
  // (which lives outside `wrapRef`'s tree) by checking a data attr.
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent | TouchEvent) => {
      const target = e.target as HTMLElement
      if (wrapRef.current?.contains(target)) return
      if (target.closest("[data-upload-panel]")) return
      setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false) }
    window.addEventListener("mousedown", onDown)
    window.addEventListener("touchstart", onDown, { passive: true })
    window.addEventListener("keydown", onKey)
    return () => {
      window.removeEventListener("mousedown", onDown)
      window.removeEventListener("touchstart", onDown)
      window.removeEventListener("keydown", onKey)
    }
  }, [open])

  const elapsedMs   = startedAt ? Date.now() - startedAt : 0
  const elapsedText = startedAt ? formatElapsed(elapsedMs) : null

  const shortName = useMemo(() => {
    if (!fileName) return "your file"
    return fileName.length > 28 ? fileName.slice(0, 25) + "…" : fileName
  }, [fileName])

  if (status === "idle") return null

  // Aperture-Light tint set — was a mix of pastel rose/emerald/violet
  // tailwind variants; on cream surfaces those read as washed-out.
  // Locked to the canonical brand hexes so done/error states actually
  // pop. Done text bumped from emerald-200 (light pastel) to #0F5132.
  // "pending" reuses the emerald tint with a subtle amber hairline —
  // it's a "ready, awaiting your call" state, not a warning. The chip
  // copy carries the meaning ("From your /try preview") so the visual
  // stays in the calm-emerald lane.
  const pillClass =
    status === "pending"
      ? "border-[#0F5132]/35 bg-[#FBF8F0] text-[#0A0A0A] hover:bg-[#0F5132]/10"
    : status === "processing"
      ? "border-[#0F5132]/25 bg-[#0F5132]/10 text-[#0A0A0A] hover:bg-[#0F5132]/15"
    : status === "done"
      ? "border-[#0F5132]/25 bg-[#0F5132]/10 text-[#0F5132] hover:bg-[#0F5132]/15"
      : "border-[#A02B1F]/25 bg-[#A02B1F]/10 text-[#A02B1F] hover:bg-[#A02B1F]/15"

  const ringColor =
    status === "pending"    ? "#0F5132"
    : status === "processing" ? "#0F5132"
    : status === "done"     ? "#0F5132"
                            : "#A02B1F"

  const stateAccent =
    status === "pending"    ? "via-[#0F5132]/70"
    : status === "processing" ? "via-[#0F5132]/70"
    : status === "done"     ? "via-[#0F5132]/70"
                            : "via-[#A02B1F]/70"

  async function handleCancel() {
    if (!jobId || cancelling) return
    setCancelling(true)
    const id = Toast.loading("Cancelling job...")
    try {
      const res = await fetch("/api/jobs/cancel", {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ jobId }),
      })
      Toast.dismiss(id)
      if (!res.ok) {
        const j = await res.json().catch(() => ({}))
        throw new Error(j?.error ?? "Cancel failed")
      }
      Toast.success("Job cancelled")
      dismissStatus()
      setOpen(false)
    } catch (err) {
      Toast.dismiss(id)
      Toast.error("Cancel failed", err instanceof Error ? err.message : undefined)
    } finally {
      setCancelling(false)
    }
  }

  return (
    <div ref={wrapRef} className="relative">
      {/* ───── DESKTOP CHIP (sm+) ───── */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`
          hidden sm:inline-flex items-center gap-2
          h-8 pl-2.5 pr-2.5 rounded-full
          border text-[11.5px] font-medium
          max-w-[260px] min-w-0
          transition-colors duration-150 cursor-pointer
          ${pillClass}
        `}
        aria-haspopup="dialog"
        aria-expanded={open}
        title={fileName ?? "Upload status"}
      >
        {status === "pending" ? (
          <>
            <span className="relative flex h-1.5 w-1.5 flex-shrink-0">
              <span className="absolute inset-0 rounded-full bg-[#0F5132] opacity-60 animate-pulse" />
              <span className="relative rounded-full h-1.5 w-1.5 bg-[#0F5132]" />
            </span>
            <span className="truncate min-w-0">
              {fileName ? `Ready: ${shortName}` : "CSV ready to process"}
            </span>
            <span className="flex-shrink-0 font-semibold text-[#0F5132]">
              from /try
            </span>
          </>
        ) : status === "processing" ? (
          <>
            <span className="relative flex h-1.5 w-1.5 flex-shrink-0">
              <span className="absolute inset-0 rounded-full bg-[#34D399] animate-ping opacity-75" />
              <span className="relative rounded-full h-1.5 w-1.5 bg-[#34D399]" />
            </span>
            <span className="truncate min-w-0">
              {fileName ? `Processing ${shortName}` : "Processing"}
            </span>
            <span className="tabular-nums font-semibold flex-shrink-0">
              {Math.round(progress)}%
            </span>
          </>
        ) : status === "done" ? (
          <>
            <CheckCircle2 size={11} className="flex-shrink-0" />
            <span className="truncate min-w-0">
              {fileName ? `${shortName} ready` : "Done"}
            </span>
          </>
        ) : (
          <>
            <XCircle size={11} className="flex-shrink-0" />
            <span className="truncate min-w-0">Upload failed</span>
          </>
        )}
        <ChevronDown
          size={11}
          className={`flex-shrink-0 opacity-60 transition-transform duration-150 ${open ? "rotate-180" : ""}`}
        />
      </button>

      {/* ───── MOBILE CHIP ───── */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`
          sm:hidden relative flex items-center justify-center
          w-8 h-8 rounded-full cursor-pointer
          transition-colors duration-150
          ${status === "pending"    ? "bg-[#FBF8F0] ring-1 ring-[#0F5132]/30 hover:bg-[#0F5132]/10"
            : status === "processing" ? "bg-[#0F5132]/10 hover:bg-[#0F5132]/15"
            : status === "done"     ? "bg-[#0F5132]/10 hover:bg-[#0F5132]/15"
                                    : "bg-[#A02B1F]/10 hover:bg-[#A02B1F]/15"}
        `}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={
          status === "pending"    ? "CSV ready to process"
          : status === "processing" ? `Processing ${Math.round(progress)}%`
          : status === "done"     ? "Upload done"
                                  : "Upload failed"
        }
      >
        {status === "pending" && (
          <span className="relative flex items-center justify-center">
            <FileSpreadsheet size={13} className="text-[#0F5132]" strokeWidth={2} />
            <span className="absolute -top-0.5 -right-0.5 h-1.5 w-1.5 rounded-full bg-[#0F5132] animate-pulse" />
          </span>
        )}
        {status === "processing" && (
          <>
            <ProgressRing progress={progress} color={ringColor} />
            <Upload size={11} className="text-[#0F5132]" strokeWidth={2.25} />
          </>
        )}
        {status === "done"  && <CheckCircle2 size={14} className="text-[#0F5132]" strokeWidth={2.25} />}
        {status === "error" && <XCircle      size={14} className="text-[#A02B1F]" strokeWidth={2.25} />}
      </button>

      {/* ───── DESKTOP POPOVER ───── */}
      {open && (
        <div
          data-upload-panel
          role="dialog"
          aria-label="Upload status"
          className="
            hidden sm:block
            absolute right-0 top-[calc(100%+8px)] z-[60]
            w-[380px] rounded-2xl overflow-hidden
            border border-[rgba(10,10,10,0.10)]
            bg-[#FBF8F0]/95 backdrop-blur-xl
            shadow-2xl shadow-black/50
            animate-in fade-in slide-in-from-top-2 duration-150
          "
        >
          <div className={`h-px bg-gradient-to-r from-transparent ${stateAccent} to-transparent`} />
          <PanelBody
            status={status}
            progress={progress}
            fileName={fileName}
            shortName={shortName}
            startedAt={startedAt}
            elapsedText={elapsedText}
            elapsedMs={elapsedMs}
            errorMessage={errorMessage}
            notice={notice}
            jobId={jobId}
            cancelling={cancelling}
            stageTimes={stageTimes.current}
            pending={pending}
            onClose={() => setOpen(false)}
            onDismiss={() => { setOpen(false); dismissStatus() }}
            onCancel={handleCancel}
            onRetry={() => {
              dismissStatus()
              setTimeout(() => retryLastUpload(), 50)
              setOpen(false)
            }}
            onConfirmPending={() => {
              if (!pending) return
              setOpen(false)
              pending.onConfirm()
            }}
            onDiscardPending={() => {
              if (!pending) return
              setOpen(false)
              pending.onDiscard()
            }}
          />
        </div>
      )}

      {/* ───── MOBILE BOTTOM SHEET ───── */}
      {open && (
        <>
          <div
            className="sm:hidden fixed inset-0 z-[59] bg-black/45 backdrop-blur-sm animate-in fade-in duration-150"
            onClick={() => setOpen(false)}
            aria-hidden
          />
          <div
            data-upload-panel
            role="dialog"
            aria-label="Upload status"
            className="
              sm:hidden fixed left-3 right-3 z-[60]
              bottom-[calc(0.75rem+env(safe-area-inset-bottom))]
              rounded-2xl overflow-hidden
              border border-[rgba(10,10,10,0.10)]
              bg-[#FBF8F0]/95 backdrop-blur-xl
              shadow-2xl shadow-black/60
              animate-in fade-in slide-in-from-bottom-3 duration-200
            "
          >
            <div className={`h-px bg-gradient-to-r from-transparent ${stateAccent} to-transparent`} />
            <div className="flex justify-center pt-2">
              <span className="block w-9 h-[3px] rounded-full bg-white/[0.10]" aria-hidden />
            </div>
            <PanelBody
              status={status}
              progress={progress}
              fileName={fileName}
              shortName={shortName}
              startedAt={startedAt}
              elapsedText={elapsedText}
              elapsedMs={elapsedMs}
              errorMessage={errorMessage}
              notice={notice}
              jobId={jobId}
              cancelling={cancelling}
              stageTimes={stageTimes.current}
              pending={pending}
              onClose={() => setOpen(false)}
              onDismiss={() => { setOpen(false); dismissStatus() }}
              onCancel={handleCancel}
              onRetry={() => {
                dismissStatus()
                setTimeout(() => retryLastUpload(), 50)
                setOpen(false)
              }}
              onConfirmPending={() => {
                if (!pending) return
                setOpen(false)
                pending.onConfirm()
              }}
              onDiscardPending={() => {
                if (!pending) return
                setOpen(false)
                pending.onDiscard()
              }}
            />
          </div>
        </>
      )}
    </div>
  )
}

// ──────────────────────────────────────────────────────────────────
// PANEL BODY — shared between desktop popover & mobile sheet.
// ──────────────────────────────────────────────────────────────────
function PanelBody({
  status, progress, fileName, shortName, startedAt, elapsedText, elapsedMs,
  errorMessage, notice, jobId, cancelling, stageTimes,
  pending,
  onClose, onDismiss, onCancel, onRetry,
  onConfirmPending, onDiscardPending,
}: {
  status:       "pending" | "processing" | "done" | "error" | "idle"
  progress:     number
  fileName:     string | null
  shortName:    string
  startedAt:    number | null
  elapsedText:  string | null
  elapsedMs:    number
  errorMessage: string | null
  notice:       string | null
  jobId:        string | null
  cancelling:   boolean
  stageTimes:   Record<string, number>
  pending:      {
    fileName:    string
    fileSize:    number
    rowEstimate: number
    source:      "try-preview"
  } | null
  onClose:      () => void
  onDismiss:    () => void
  onCancel:     () => void
  onRetry:      () => void
  onConfirmPending: () => void
  onDiscardPending: () => void
}) {
  // Pending body — the user just landed from /try and we need their
  // OK before pushing the file through the worker. Shares the chip
  // surface with processing/done/error rather than mounting a separate
  // banner / modal / floating card.
  if (status === "pending" && pending) {
    return (
      <div className="px-4 pt-4 pb-3.5">
        <div className="flex items-start gap-3">
          <span
            className="relative flex h-9 w-9 items-center justify-center rounded-lg border bg-[#0F5132]/15 border-[#0F5132]/25 flex-shrink-0 mt-0.5"
          >
            <FileSpreadsheet size={15} className="text-[#0F5132]" strokeWidth={1.75} />
          </span>
          <div className="flex-1 min-w-0">
            <p
              className="text-[9.5px] font-bold uppercase tracking-[0.16em] text-[rgba(10,10,10,0.50)]"
              style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
            >
              <span className="text-[#0F5132] mr-1.5">—</span>
              From your /try preview
            </p>
            <p className="text-[13px] font-semibold text-[#0A0A0A] truncate leading-tight mt-1" title={pending.fileName}>
              {pending.fileName}
            </p>
            <p
              className="text-[11px] text-[rgba(10,10,10,0.55)] mt-0.5 tabular-nums"
              style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", letterSpacing: "0.03em" }}
            >
              ~{pending.rowEstimate.toLocaleString()} rows · {(pending.fileSize / 1024).toFixed(0)} KB
            </p>
          </div>
          <button
            onClick={onDiscardPending}
            className="flex-shrink-0 p-1.5 -mt-1 -mr-1 rounded-md text-[rgba(10,10,10,0.45)] hover:text-[#0A0A0A] hover:bg-[rgba(10,10,10,0.06)] transition-colors cursor-pointer"
            aria-label="Discard"
          >
            <X size={13} />
          </button>
        </div>

        <p className="mt-3 text-[12px] leading-relaxed text-[rgba(10,10,10,0.65)]">
          We saved this from your anonymous preview. Process it now to run
          the full eight-validator stack and get the audit trail.
        </p>

        <div className="mt-3.5 flex items-center gap-2">
          <button
            type="button"
            onClick={onDiscardPending}
            className="inline-flex items-center justify-center px-3 py-1.5 rounded-md border border-[rgba(10,10,10,0.14)] bg-transparent text-[12.5px] font-semibold text-[rgba(10,10,10,0.65)] hover:bg-[rgba(10,10,10,0.04)] hover:text-[#0A0A0A] transition-colors cursor-pointer"
          >
            Not now
          </button>
          <button
            type="button"
            onClick={onConfirmPending}
            className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md bg-[#0F5132] text-[#FAF7EE] text-[12.5px] font-bold hover:bg-[#0A3D26] transition-colors cursor-pointer"
            style={{ boxShadow: "0 6px 14px -8px rgba(15,81,50,0.55)" }}
          >
            Process full CSV
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="px-4 pt-4 pb-3.5">
      {/* ─────────────── HEADER ─────────────── */}
      <div className="flex items-start gap-3">
        <div className="relative flex-shrink-0 mt-0.5">
          {status === "processing" && (
            <span
              className="absolute inset-0 rounded-lg bg-[#0F5132]/20 animate-ping"
              style={{ animationDuration: "1.8s" }}
            />
          )}
          {/* Aperture-Light tint set: cream surface needs ink text +
              emerald/red accents in the canonical hexes, NOT
              tailwind's pastel rose/emerald variants which read as
              washed-out on cream. */}
          <span
            className={`
              relative flex h-9 w-9 items-center justify-center rounded-lg border
              ${status === "processing" ? "bg-[#0F5132]/15 border-[#0F5132]/25"
                : status === "done"     ? "bg-[#0F5132]/15 border-[#0F5132]/25"
                                        : "bg-[#A02B1F]/10 border-[#A02B1F]/25"}
            `}
          >
            {status === "processing" && <FileSpreadsheet size={15} className="text-[#0F5132]" strokeWidth={1.75} />}
            {status === "done"       && <CheckCircle2    size={16} className="text-[#0F5132]" strokeWidth={2} />}
            {status === "error"      && <XCircle         size={16} className="text-[#A02B1F]"  strokeWidth={2} />}
          </span>
        </div>

        <div className="flex-1 min-w-0">
          {/* Filename — was text-white/90 (left over from a dark-mode
              variant of this chip). Cream surface needs ink. */}
          <p className="text-[13px] font-semibold text-[#0A0A0A] truncate leading-tight">
            {fileName ?? (status === "done" ? "Dataset ready" : status === "error" ? "Upload failed" : "Cleaning your list")}
          </p>
          <div className="flex items-center gap-1.5 mt-1 flex-wrap">
            {status === "processing" && (
              <>
                <Loader2 size={10} className="text-[#0F5132]/70 animate-spin flex-shrink-0" />
                <span className="text-[11.5px] text-[rgba(10,10,10,0.65)]">Cleaning your list</span>
              </>
            )}
            {status === "done"  && <span className="text-[11.5px] font-medium text-[#0F5132]">Cleaned successfully</span>}
            {/* Error subtext — was text-rose-300/85 which reads as a
                pink wash on cream (~2.5:1 contrast). Switched to the
                canonical Aperture danger ink. */}
            {status === "error" && <span className="text-[11.5px] font-medium text-[#A02B1F]">{errorMessage ?? "Something went wrong"}</span>}
          </div>
        </div>

        {/* Big elapsed counter — anchors the panel and feels "live" */}
        {status === "processing" && elapsedText && (
          <div className="flex-shrink-0 text-right">
            <p className="text-[15px] font-semibold text-[#0A0A0A] tabular-nums leading-none">
              {elapsedText}
            </p>
            <p className="text-[10px] text-[rgba(10,10,10,0.45)] mt-0.5 uppercase tracking-wider">elapsed</p>
          </div>
        )}
        {status === "done" && elapsedText && (
          <div className="flex-shrink-0 text-right">
            <p className="text-[15px] font-semibold text-[#0F5132] tabular-nums leading-none">
              {elapsedText}
            </p>
            <p className="text-[10px] text-[rgba(10,10,10,0.45)] mt-0.5 uppercase tracking-wider">total</p>
          </div>
        )}
        {status !== "processing" && (
          <button
            onClick={onDismiss}
            className="flex-shrink-0 p-1.5 -mt-1 -mr-1 rounded-md text-[rgba(10,10,10,0.45)] hover:text-[#0A0A0A] hover:bg-[rgba(10,10,10,0.06)] transition-colors cursor-pointer"
            aria-label="Dismiss"
          >
            <X size={13} />
          </button>
        )}
      </div>

      {/* ─────────────── PROGRESS BAR (slim, only while processing) ─────────────── */}
      {status === "processing" && (
        <div className="relative h-[3px] mt-4 rounded-full bg-[rgba(10,10,10,0.06)] overflow-hidden">
          <div
            className="absolute top-0 left-0 h-full rounded-full bg-gradient-to-r from-[#1F7A4E] to-[#0F5132] blur-[3px] opacity-70 transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
          <div
            className="absolute top-0 left-0 h-full rounded-full bg-gradient-to-r from-[#1F7A4E] to-[#0F5132] transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
          <div
            className="absolute top-0 h-full w-12 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer"
            style={{ left: `${progress - 10}%` }}
          />
        </div>
      )}

      {/* ─────────────── STAGE TIMELINE (the "ultra pro" upgrade) ─────────────── */}
      {status === "processing" && (
        <ul className="mt-4 space-y-2">
          {STAGES.map((stage, i) => {
            const state = stageStateFor(progress, i)
            const completedAt = stageTimes[stage.key]
            const stageTime = completedAt && startedAt
              ? formatElapsed(completedAt - startedAt)
              : null

            return (
              <li
                key={stage.key}
                className={`
                  flex items-center gap-2.5 text-[11.5px]
                  transition-colors duration-300
                  ${state === "done"    ? "text-[rgba(10,10,10,0.70)]"
                    : state === "active" ? "text-[#0A0A0A] font-semibold"
                                         : "text-[rgba(10,10,10,0.42)]"}
                `}
              >
                {/* Status icon — stable column for visual alignment */}
                <span className="flex-shrink-0 flex items-center justify-center w-4 h-4">
                  {state === "done" && (
                    <span className="flex h-3.5 w-3.5 items-center justify-center rounded-full bg-[#0F5132]/20 border border-[#0F5132]/40">
                      <svg viewBox="0 0 12 12" fill="none" className="w-2 h-2">
                        <path d="M2.5 6.5L5 9L9.5 3.5" stroke="#34D399" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </span>
                  )}
                  {state === "active" && (
                    <span className="relative flex h-3.5 w-3.5 items-center justify-center">
                      <span className="absolute inset-0 rounded-full bg-[#0F5132]/20 animate-ping" style={{ animationDuration: "1.5s" }} />
                      <span className="relative h-2 w-2 rounded-full bg-[#1F7A4E]" />
                    </span>
                  )}
                  {state === "pending" && (
                    <span className="h-3.5 w-3.5 rounded-full border border-[rgba(10,10,10,0.10)]" />
                  )}
                </span>

                <span className="flex-1 min-w-0 truncate">{stage.label}</span>

                {stageTime && state === "done" && (
                  <span className="flex-shrink-0 text-[10.5px] text-[rgba(10,10,10,0.42)] tabular-nums">
                    {stageTime}
                  </span>
                )}
                {state === "active" && (
                  <span className="flex-shrink-0 text-[10.5px] text-[#0F5132]/80 tabular-nums">
                    {Math.round(progress)}%
                  </span>
                )}
              </li>
            )
          })}
        </ul>
      )}

      {/* ─────────────── DONE summary ─────────────── */}
      {status === "done" && (
        <p className="text-[12px] text-[rgba(10,10,10,0.65)] mt-3 leading-relaxed">
          <span className="text-[rgba(10,10,10,0.75)] font-medium">{shortName}</span> is ready in your datasets.
        </p>
      )}

      {/* ─────────────── NOTICE ─────────────── */}
      {notice && status === "processing" && (
        <div className="flex items-center gap-1.5 mt-3 pt-3 border-t border-[rgba(10,10,10,0.06)]">
          <Mail size={11} className="text-[rgba(10,10,10,0.45)] flex-shrink-0" />
          <span className="text-[11.5px] text-[rgba(10,10,10,0.65)] truncate">{notice}</span>
        </div>
      )}

      {/* ─────────────── ACTIONS BAR ─────────────── */}
      <div className="flex items-center justify-between gap-2 mt-4 pt-3 border-t border-[rgba(10,10,10,0.08)]">
        {status === "processing" ? (
          <>
            <button
              type="button"
              onClick={onCancel}
              disabled={!jobId || cancelling}
              className="
                inline-flex items-center gap-1.5 px-2 py-1 rounded-md
                text-[11.5px] font-medium
                text-[rgba(10,10,10,0.65)] hover:text-[#A02B1F] hover:bg-[#A02B1F]/[0.08]
                transition-colors duration-150 cursor-pointer
                disabled:opacity-40 disabled:cursor-not-allowed
              "
            >
              {cancelling ? <Loader2 size={11} className="animate-spin" /> : <Ban size={11} />}
              {cancelling ? "Cancelling…" : "Cancel job"}
            </button>
            <Link
              href="/dashboard/datasets"
              onClick={onClose}
              className="
                inline-flex items-center gap-1 px-2 py-1 rounded-md
                text-[11.5px] font-medium text-[rgba(10,10,10,0.65)]
                hover:text-[#0F5132]/90 hover:bg-[rgba(10,10,10,0.04)]
                transition-colors duration-150
              "
            >
              View in Datasets
              <ExternalLink size={10} />
            </Link>
          </>
        ) : status === "done" ? (
          <>
            <span />
            <Link
              href="/dashboard/datasets"
              onClick={onDismiss}
              className="
                inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md
                text-[11.5px] font-semibold
                border border-[#0F5132]/30 bg-[#0F5132]/[0.08] text-[#0F5132]
                hover:bg-[#0F5132]/[0.15] hover:border-[#0F5132]/50
                transition-colors duration-150 cursor-pointer
              "
            >
              Open dataset
              <ExternalLink size={11} />
            </Link>
          </>
        ) : (
          <>
            <span />
            {hasLastUpload() && (
              <button
                onClick={onRetry}
                className="
                  inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md
                  text-[11.5px] font-semibold
                  border border-[#A02B1F]/30 bg-[#A02B1F]/[0.08] text-[#A02B1F]
                  hover:bg-[#A02B1F]/[0.15] hover:border-[#A02B1F]/50
                  transition-colors duration-150 cursor-pointer
                "
              >
                <RotateCcw size={11} strokeWidth={2.25} />
                Retry upload
              </button>
            )}
          </>
        )}
      </div>
    </div>
  )
}
